import { useContext, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { AuthContext } from '../context/AuthContext';
import LoadingSpinner from './global/LoadingSpinner';
import { getSettings } from '../services/sprites';

/**
 * Route wrapper for the sprite editor.
 *
 * Requires authentication, then asks the server whether this account may
 * submit — a rule that depends on both the account's role and an
 * admin-toggleable global flag, so it cannot be decided from the cached user
 * object alone.
 *
 * THIS IS NOT A SECURITY BOUNDARY. It decides what renders. Anyone can call
 * the API directly, which is why server/middleware/spriteAccess.js re-checks
 * on every request. This exists so contributors are not shown a page that
 * would reject everything they do.
 */
function ContributorRoute({ children }) {
  const { token, loading } = useContext(AuthContext);
  const [allowed, setAllowed] = useState(null); // null = still asking

  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    getSettings(token)
      .then((settings) => {
        if (!cancelled) setAllowed(Boolean(settings.canSubmit));
      })
      .catch(() => {
        // Treat an unreachable settings endpoint as "not allowed" rather than
        // rendering an editor whose every submission would fail.
        if (!cancelled) setAllowed(false);
      });

    // eslint-disable-next-line consistent-return
    return () => { cancelled = true; };
  }, [token]);

  if (loading || (token && allowed === null)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!token) return <Navigate to="/login" replace />;
  if (!allowed) return <Navigate to="/" replace />;

  return children;
}

ContributorRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ContributorRoute;
